import { createSlice } from '@reduxjs/toolkit';
import { fetchIntegrationsThunk } from '../thunks/integration';
import {
    composeEmailThunk,
    deleteMailThunk,
    fetchMailMessagesThunk,
    fetchThreadMessagesThunk,
    fetchUserIntegrationThunk,
    fetchUserMailsThunk,
    markAllMailsAsReadThunk,
    replyToThreadThunk,
    sendBulkEmailsThunk
} from '../thunks/email';
import { EmailObject, EmailState, MailMessageObject } from '../../params/redux/email';

// Initial state with default values for authentication and user info
const initialState: EmailState = {
    emails: [],
    selectedEmail: null,
    mailMessages: [],
    flags: {
        isIntegrated: false,
        composing: false,
        isInbox: true
    },
    loaders: {
        isLoading: false,
        isCreating: false,
        isUpdating: false,
        isDeleting: false,
        isSearching: false,
        isInserting: false
    }
};

// Slice for app-related states and actions
const emailSlice = createSlice({
    // Slice name
    name: 'email',
    // inital state for the slice
    initialState,
    reducers: {
        settingSelectedEmail: (state, action) => {
            state.selectedEmail = action.payload;
        },
        settingComposingFlagEmail: (state, action) => {
            state.flags.composing = action.payload;
        },
        settingIsInboxFlagEmail: (state, action) => {
            state.flags.isInbox = action.payload;
        },
        // Resets loading states related to app actions
        resettingEmailLoader: (state) => {
            state.loaders = initialState.loaders;
        },
        // Resets all app data to the initial state
        resettingEmailData: () => initialState
    },
    extraReducers: (builder) => {
        // Handle async actions for login
        builder
            .addCase(fetchUserIntegrationThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isLoading = true;
            })
            .addCase(fetchUserIntegrationThunk.fulfilled, (state, action) => {
                const data = action.payload as string[];
                const isExisting = data.find((item) => item === 'email');
                if (isExisting) {
                    state.flags.isIntegrated = true;
                } else {
                    state.flags.isIntegrated = false;
                }
            })
            .addCase(fetchUserIntegrationThunk.rejected, (state) => {
                state.flags.isIntegrated = false;
            });
        builder
            .addCase(fetchIntegrationsThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isLoading = true;
            })
            .addCase(fetchIntegrationsThunk.fulfilled, (state, action) => {
                const data = action.payload as string[];
                const isExisting = data.find((item) => item === 'email');
                if (isExisting) {
                    state.flags.isIntegrated = true;
                } else {
                    state.flags.isIntegrated = false;
                }
            })
            .addCase(fetchIntegrationsThunk.rejected, (state) => {
                state.flags.isIntegrated = false;
            });
        builder
            .addCase(fetchUserMailsThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isLoading = true;
            })
            .addCase(fetchUserMailsThunk.fulfilled, (state, action) => {
                state.emails = action.payload as EmailObject[];
            })
            .addCase(fetchUserMailsThunk.rejected, (state) => {
                state.emails = [];
            });
        builder
            .addCase(composeEmailThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isCreating = true;
            })
            .addCase(composeEmailThunk.fulfilled, () => {
                //
            })
            .addCase(composeEmailThunk.rejected, () => {
                //
            });
        builder
            .addCase(replyToThreadThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isCreating = true;
            })
            .addCase(replyToThreadThunk.fulfilled, () => {
                //
            })
            .addCase(replyToThreadThunk.rejected, () => {
                //
            });
        builder
            .addCase(fetchMailMessagesThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isLoading = true;
            })
            .addCase(fetchMailMessagesThunk.fulfilled, (state, action) => {
                state.mailMessages = action.payload as MailMessageObject[];
            })
            .addCase(fetchMailMessagesThunk.rejected, (state) => {
                state.mailMessages = [];
            });
        builder
            .addCase(markAllMailsAsReadThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isLoading = true;
            })
            .addCase(markAllMailsAsReadThunk.fulfilled, () => {
                //
            })
            .addCase(markAllMailsAsReadThunk.rejected, () => {
                //
            });
        builder
            .addCase(deleteMailThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isDeleting = true;
            })
            .addCase(deleteMailThunk.fulfilled, () => {
                //
            })
            .addCase(deleteMailThunk.rejected, () => {
                //
            });
        builder
            .addCase(fetchThreadMessagesThunk.pending, () => {
                // Set loading flag during login request
            })
            .addCase(fetchThreadMessagesThunk.fulfilled, () => {
                //
            })
            .addCase(fetchThreadMessagesThunk.rejected, () => {
                //
            });
            builder
            .addCase(sendBulkEmailsThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isCreating = true;
            })
            .addCase(sendBulkEmailsThunk.fulfilled, () => {
                //
            })
            .addCase(sendBulkEmailsThunk.rejected, () => {
                //
            });
    }
});

// Exporting actions for updating app state
export const {
    settingComposingFlagEmail,
    settingIsInboxFlagEmail,
    settingSelectedEmail,
    resettingEmailData,
    resettingEmailLoader
} = emailSlice.actions;

// Exporting the reducer to be used in the store
export default emailSlice.reducer;
